import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';
import { notifySupportApiNetworkError, notifySupportApiServerError } from '../utils/supportApiErrorNotifier';
import { calculateCompletePricingWithSeasons } from '../services/pricingService';

const BookingContext = createContext();

const STORAGE_KEY = 'bike-rental-booking-data';

const initialBookingData = {
  selectedVehicles: [],
  selectedAccessories: {},
  accessories: [],
  availabilityIssues: null,
  inseamCmByVehicleId: {},
  dates: {
    start: null,
    end: null,
    startTime: null,
    endTime: null
  },
  pricing: {
    durationInDays: 0,
    vehicles: {
      items: [],
      total: 0
    },
    accessories: {
      items: [],
      total: 0,
      insuranceTotal: 0
    },
    grandTotal: 0
  },
  deliveryOption: null,
  customerInfo: {
    name: '',
    email: '',
    phone: '',
    phonePrefix: '+34',
    dni: '',
    address: ''
  },
  paymentInfo: {
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  },
  temporaryBlockIds: [],
  bookingId: null,
  isCompleted: false
};

export const BookingProvider = ({ children }) => {
  const [bookingData, setBookingData] = useState(initialBookingData);
  const [isLoaded, setIsLoaded] = useState(false);

  // Cargar datos del localStorage al inicializar
  useEffect(() => {
    const loadBookingData = () => {
      try {
        const savedData = localStorage.getItem(STORAGE_KEY);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          
          // Si la reserva está completada, limpiar todos los datos al recargar
          if (parsedData.isCompleted) {
            console.log('Reserva completada detectada, limpiando datos al recargar');
            localStorage.removeItem(STORAGE_KEY);
            setBookingData(initialBookingData);
            return;
          }
          
          // Validar fechas - si la fecha de inicio es anterior a hoy, limpiar datos
          if (parsedData.dates?.start) {
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Establecer a medianoche para comparación
            const startDate = new Date(parsedData.dates.start);
            startDate.setHours(0, 0, 0, 0);
            
            // Solo rechazar fechas anteriores a hoy (no incluyendo hoy)
            if (startDate < today) {
              console.log('Fechas guardadas son anteriores a hoy, limpiando datos de reserva');
              localStorage.removeItem(STORAGE_KEY);
              setBookingData(initialBookingData);
            } else {
              console.log('Fechas válidas (hoy o futuro), cargando datos guardados');
              setBookingData(prev => ({
                ...prev,
                ...parsedData
              }));
            }
          } else {
            // No hay fechas guardadas, cargar datos normalmente
            setBookingData(prev => ({
              ...prev,
              ...parsedData
            }));
          }
        }
      } catch (error) {
        console.error('Error al cargar datos del localStorage:', error);
        // En caso de error, limpiar localStorage y usar datos iniciales
        localStorage.removeItem(STORAGE_KEY);
        setBookingData(initialBookingData);
      } finally {
        setIsLoaded(true);
      }
    };

    loadBookingData();
  }, []);

  // Guardar datos en localStorage cuando cambien
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(bookingData));
      } catch (error) {
        console.error('Error al guardar datos en localStorage:', error);
      }
    }
  }, [bookingData, isLoaded]);

  const updateBookingData = useCallback((updates) => {
    setBookingData(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Función para calcular precios con temporadas
  const calculatePricingWithSeasons = async (vehicles, selectedAccessories, dates, accessoriesList, deliveryTypeOrOption = 'pickup') => {
    try {
      if (!dates?.start || !dates?.end || !vehicles || vehicles.length === 0) {
        return {
          durationInDays: 0,
          vehicles: { items: [], total: 0 },
          accessories: { items: [], total: 0 },
          delivery: { cost: 0 },
          grandTotal: 0,
          seasons: [],
          pricingType: 'default'
        };
      }

      const pricing = await calculateCompletePricingWithSeasons(
        vehicles,
        selectedAccessories,
        dates,
        accessoriesList,
        deliveryTypeOrOption
      );

      return pricing;
    } catch (error) {
      console.error('Error calculando precios con temporadas:', error);
      
      // Fallback a cálculo local
      const { calculateCompletePricing } = await import('../services/pricingService');
      return calculateCompletePricing(vehicles, selectedAccessories, dates, accessoriesList, deliveryTypeOrOption);
    }
  };

  // Función para actualizar precios cuando cambien las fechas o vehículos
  const updatePricingWithSeasons = async (newData) => {
    try {
      const updatedData = { ...bookingData, ...newData };
      
      // Solo recalcular si hay vehículos y fechas
      if (updatedData.selectedVehicles?.length > 0 && updatedData.dates?.start && updatedData.dates?.end) {
        try {
          const pricing = await calculateCompletePricingWithSeasons(
            updatedData.selectedVehicles,
            updatedData.selectedAccessories || {},
            updatedData.dates,
            updatedData.accessories || [],
            updatedData.deliveryOption || 'pickup'
          );
          
          updatedData.pricing = pricing;
        } catch (pricingError) {
          console.warn('Error calculando precios estacionales, usando cálculo local:', pricingError);
          
          // Fallback a cálculo local si hay error con precios estacionales
          const { calculateCompletePricing } = await import('../services/pricingService');
          const localPricing = calculateCompletePricing(
            updatedData.selectedVehicles,
            updatedData.selectedAccessories || {},
            updatedData.dates,
            updatedData.accessories || [],
            updatedData.deliveryOption || 'pickup'
          );
          
          updatedData.pricing = localPricing;
        }
      }
      
      setBookingData(updatedData);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
    } catch (error) {
      console.error('Error actualizando precios:', error);
      // Actualizar sin recalcular precios
      setBookingData(prevData => {
        const updatedData = { ...prevData, ...newData };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
        return updatedData;
      });
    }
  };

  const clearBookingData = () => {
    setBookingData(initialBookingData);
    localStorage.removeItem(STORAGE_KEY);
  };

  const markBookingAsCompleted = () => {
    setBookingData(prev => ({
      ...prev,
      isCompleted: true
    }));
  };

  const clearAvailabilityCache = () => {
    // Esta función se puede usar para limpiar caché de disponibilidad si es necesario
    // Por ahora es una función vacía, pero se puede implementar lógica de caché aquí
    console.log('Clearing availability cache');
  };

  const clearTemporaryBooking = async () => {
    try {
      // Si hay una reserva temporal activa, eliminarla del backend
      if (bookingData.bookingId && bookingData.temporaryBlockIds?.length > 0) {
        console.log('Eliminando reserva temporal del backend:', bookingData.bookingId);
        
        const response = await fetch(buildApiUrl(`${API_ENDPOINTS.BOOKING_TEMP}/${bookingData.bookingId}`), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          notifySupportApiServerError(response.status);
          console.warn('No se pudo eliminar la reserva temporal del backend');
        } else {
          console.log('Reserva temporal eliminada del backend');
        }
      }
    } catch (error) {
      notifySupportApiNetworkError(error);
      console.warn('Error al eliminar reserva temporal del backend:', error);
    }

    // Limpiar los datos de la reserva temporal en el frontend
    setBookingData(prev => ({
      ...prev,
      temporaryBlockIds: [],
      bookingId: null
    }));
  };

  const resetToStep = async (step) => {
    // Limpiar datos según el paso al que se retrocede
    const resetData = { ...bookingData };
    
    // Si hay una reserva temporal activa y se navega hacia atrás desde pasos posteriores al cliente,
    // eliminar la reserva temporal del backend
    if (bookingData.bookingId && bookingData.temporaryBlockIds?.length > 0 && step < 4) {
      console.log('Eliminando reserva temporal al navegar hacia atrás desde paso posterior al cliente');
      await clearTemporaryBooking();
    }
    
    switch (step) {
      case 0: // Fechas - Limpiar todo
        resetData.selectedVehicles = [];
        resetData.selectedAccessories = {};
        resetData.accessories = [];
        resetData.deliveryOption = null;
        resetData.customerInfo = initialBookingData.customerInfo;
        resetData.paymentInfo = initialBookingData.paymentInfo;
        resetData.inseamCmByVehicleId = {};
        resetData.temporaryBlockIds = [];
        resetData.bookingId = null;
        break;
      case 1: // Bicicletas - NO limpiar accesorios, entrega ni cliente, solo datos posteriores
        // Mantener selectedAccessories, accessories, deliveryOption y customerInfo
        resetData.paymentInfo = initialBookingData.paymentInfo;
        resetData.inseamCmByVehicleId = resetData.inseamCmByVehicleId || {};
        resetData.temporaryBlockIds = [];
        resetData.bookingId = null;
        break;
      case 2: // Accesorios - NO limpiar accesorios, entrega ni cliente, solo datos posteriores
        // Mantener selectedAccessories, accessories, deliveryOption y customerInfo
        resetData.paymentInfo = initialBookingData.paymentInfo;
        resetData.inseamCmByVehicleId = resetData.inseamCmByVehicleId || {};
        resetData.temporaryBlockIds = [];
        resetData.bookingId = null;
        break;
      case 3: // Entrega - NO limpiar cliente, solo datos posteriores
        // Mantener customerInfo
        resetData.paymentInfo = initialBookingData.paymentInfo;
        resetData.inseamCmByVehicleId = resetData.inseamCmByVehicleId || {};
        resetData.temporaryBlockIds = [];
        resetData.bookingId = null;
        break;
      case 4: // Cliente - Limpiar reserva temporal y datos posteriores
        // Limpiar reserva temporal del backend y frontend
        await clearTemporaryBooking();
        resetData.paymentInfo = initialBookingData.paymentInfo;
        resetData.temporaryBlockIds = [];
        resetData.bookingId = null;
        break;
      case 5: // Pago - Limpiar reserva temporal al navegar hacia atrás
        // Limpiar reserva temporal del backend y frontend
        await clearTemporaryBooking();
        resetData.paymentInfo = initialBookingData.paymentInfo;
        break;
      // Para otros pasos no se resetea nada
    }
    
    setBookingData(resetData);
  };

  const value = {
    bookingData,
    updateBookingData,
    updatePricingWithSeasons,
    calculatePricingWithSeasons,
    clearBookingData,
    markBookingAsCompleted,
    clearAvailabilityCache,
    clearTemporaryBooking,
    resetToStep,
    isLoaded
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking debe ser usado dentro de BookingProvider');
  }
  return context;
};